import {useState} from 'react';

import {useKeenSlider} from 'keen-slider/react';

import type {Walk} from 'types/payload-types';

const PER_VIEW_XS = 1.15;
const PER_VIEW_SM = 2.15;
const PER_VIEW_MD = 3.15;
const PER_VIEW_LG = 4;

/**
 * Custom hook for the `FeaturedWalksBlock` component.
 * Manages keen-slider state, scroll detection, and dot pagination.
 *
 * @param walks Raw walks array (may contain string IDs).
 * @returns An object containing slider state and refs.
 */
export const useFeaturedWalksBlock = (walks: (Walk | string)[] | null | undefined) => {
    const populatedWalks = (walks ?? []).filter((w): w is Walk => typeof w === 'object');
    const slideCount = populatedWalks.length;
    const [canScroll, setCanScroll] = useState(slideCount > 1);
    const [activeIndex, setActiveIndex] = useState(0);

    const [sliderRef] = useKeenSlider<HTMLDivElement>({
        breakpoints: {
            '(min-width: 600px)': {
                loop: slideCount > Math.floor(PER_VIEW_SM),
                slides: {
                    perView: Math.min(slideCount, PER_VIEW_SM),
                    spacing: 16
                }
            },
            '(min-width: 900px)': {
                loop: slideCount > Math.floor(PER_VIEW_MD),
                slides: {
                    perView: Math.min(slideCount, PER_VIEW_MD),
                    spacing: 20
                }
            },
            '(min-width: 1200px)': {
                loop: slideCount > Math.floor(PER_VIEW_LG),
                slides: {
                    perView: Math.min(slideCount, PER_VIEW_LG),
                    spacing: 24
                }
            }
        },
        loop: slideCount > 1,
        slides: {
            perView: Math.min(slideCount, PER_VIEW_XS),
            spacing: 16
        },
        /**
         * Checks whether slides overflow the viewport and updates scroll state.
         *
         * @param slider The keen-slider instance.
         */
        created(slider) {
            const perView = (slider.options.slides as {perView?: number}).perView ?? 1;

            setCanScroll(slideCount > perView);
        },
        /**
         * Re-checks scrollability when breakpoint or options change.
         *
         * @param slider The keen-slider instance.
         */
        optionsChanged(slider) {
            const perView = (slider.options.slides as {perView?: number}).perView ?? 1;

            setCanScroll(slideCount > perView);
        },
        /**
         * Updates active dot index on slide change.
         *
         * @param slider The keen-slider instance.
         */
        slideChanged(slider) {
            setActiveIndex(slider.track.details.rel);
        }
    });

    return {
        activeIndex,
        canScroll,
        populatedWalks,
        sliderRef
    };
};